import * as React from "react";
import classnames from "classnames";

import { makeStyles } from "@material-ui/styles";
import Alert from "@material-ui/lab/Alert";
import MuiDivider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import MuiList from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import CircularProgress from "@material-ui/core/CircularProgress";
//icons
import ActionPrint from "@material-ui/icons/Print";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/chart/gauge";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";

import { PageFooter, Button, translate, BackButton } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import CardHeader from "../../../../components/CardHeader";
import Table from "../../../../components/Table";
import useReconciliationDetail from "./useReconciliationDetail";
import { isEmpty } from "../../../../utils/helpers";
import { OState } from "../../../../utils/types";
import { DataType } from "../microReportHelper";
import { colorSecondary, colorTomatoRed } from "../../../../utils/colors";

const dividerStyle = { backgroundColor: "rgba(200, 200, 200, 1)" };
const Divider = props => <MuiDivider style={dividerStyle} {...props} />;

const List = ({ data, rightColor, dense = false, rightVariant = "h6" }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "1em" }}>
        <Typography>No highlight available</Typography>
      </div>
    );
  }
  return (
    <MuiList dense={dense} style={{ padding: 0 }}>
      {data.map(({ title, subTitle, rightTitle }, idx) => (
        <ListItem key={idx} divider>
          <ListItemText primary={title} secondary={subTitle ? subTitle : null} />
          <ListItemSecondaryAction>
            <Typography variant={rightVariant} style={{ color: rightColor }}>
              {rightTitle}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </MuiList>
  );
};

const useStyles = makeStyles(theme => ({
  marginVertical: {
    margin: "1em 0"
  },
  table: {
    "& th, & td": {
      borderBottom: "1px solid rgba(200, 200, 200, 1)",
      borderTop: "1px solid rgba(200, 200, 200, 1)"
    },
    whiteSpace: "nowrap"
  },
  border: {
    border: "0.5px solid rgba(0, 0, 0, 0.35)"
  }
}));

const ReconciliationReportDetail = props => {
  const classes = useStyles();
  const { tabIdx, selectedKey, setSelectedKey, translate } = props;

  const {
    cutOrder,
    summaryColumns,
    summaryRows,
    cutOrderRows,
    cutOrderColumns,
    fabUsageColumns,
    fabUsageRows,
    columnsCutSummary,
    headerRows,
    rowsCutSummary,
    colsFabUtilization,
    colsRemarks,
    chartOptions,
    otherInfoList,
    highlightList,
    rowsRemarks,
    cutOrderKey,
    printing,
    setCutOrderKey,
    onPrint
  } = useReconciliationDetail(props);

  const onSummaryClick = row => {
    if (row?.isParent) return;
    setSelectedKey(row?.key);
  };

  const onDetailClick = row => {
    if (isEmpty(row.parentKey)) {
      setCutOrderKey(row.key);
    }
  };

  if (tabIdx !== 1) return <div />;

  return (
    <div>
      <div className={classes.marginVertical}>
        <MuiTable
          title="Order Summary"
          columns={summaryColumns}
          rows={summaryRows}
          searchKeys={["style", "color"]}
          onRowClick={onSummaryClick}
          rowStyle={({ row }) => (selectedKey === row?.key ? { backgroundColor: "#BFDDF8" } : {})}
          idKey="key"
          parentIdKey="parentKey"
          pageable
          defaultExpanded
        />
      </div>

      <div className={classes.marginVertical}>
        <MuiTable
          title={translate("resources.rollPlan.title.cut_orders", { _: "Cut Orders" })}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["flowRef", "customerFlowRef", "style", "styleNo"]}
          idKey="key"
          parentIdKey="parentKey"
          columns={cutOrderColumns}
          rows={cutOrderRows}
          defaultExpanded={false}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          onRowClick={onDetailClick}
          rowStyle={({ row }) => ({ backgroundColor: cutOrderKey === row.key ? "#FFEFE3" : "none" })}
          // i18nKey="resources.grouping.fields.flowInfo"
        />
      </div>

      {cutOrder?.stateValue <= OState.CUTPLAN_COMPLETED.stateValue && (
        <Alert variant="filled" severity="warning" className={classes.marginVertical}>
          <Typography variant="h6">Feedback not started yet. Reconcilation report is available after feedback is started.</Typography>
        </Alert>
      )}

      {cutOrder?.stateValue > OState.CUTPLAN_COMPLETED.stateValue && (
        <div>
          <div className={classes.marginVertical}>
            <MuiTable
              title="Fabric Usage Summary"
              toolbar
              columns={fabUsageColumns}
              rows={fabUsageRows}
              rowStyle={({ row }) => (cutOrderKey === row?.key ? { backgroundColor: "#FFEFE3" } : {})}
              idKey="key"
              i18nKey="unknown"
            />
          </div>

          <Card id="cut-summary" className={classnames(classes.marginVertical, classes.border)}>
            <CardHeader title="Cut Summary" />
            <PerfectScrollbar>
              <Table
                columns={columnsCutSummary}
                headerRows={headerRows}
                rows={rowsCutSummary}
                headerCellStyle={column => ({
                  borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                  borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                  backgroundColor: "#C6D0DA"
                })}
                cellStyle={({ column, record }) =>
                  record.name === "Total"
                    ? {
                        fontWeight: "bold",
                        borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                        borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                      }
                    : {
                        borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                        borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                      }
                }
              />
            </PerfectScrollbar>
          </Card>
          <div id="chart-container" className={classes.marginVertical}>
            {Object.keys(chartOptions).length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} id="row-1">
                  <Card className={classes.border}>
                    <CardHeader title="Fabric Utilization Summary" />
                    <CardContent style={{ padding: "0 0 8px 0" }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.FABRIC_UTILIZATION_SUMMARY]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4} id="row-2">
                  <Card className={classes.border}>
                    <CardHeader title="Wastage Summary" />
                    <CardContent style={{ padding: "0 0 8px 0" }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.WASTAGE_SUMMMARY]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4} id="row-3">
                  <Card className={classes.border}>
                    <CardHeader title="Consumption Summary" />
                    <CardContent style={{ padding: "0 0 8px 16px" }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.CONSUMPTION_SUMMARY]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {Object.keys(chartOptions).length > 0 && (
              <Grid id="row-4" container spacing={2}>
                <Grid item xs={12} sm={6} id="deviation">
                  <Card className={classes.border}>
                    <CardHeader title="Roll Width Deviation" />
                    <CardContent style={{ padding: 0 }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.ROLL_WIDTH_DEVIATION]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3} id="Variation">
                  <Card className={classes.border}>
                    <CardHeader title="Roll Length Variation" />
                    <CardContent style={{ padding: 0 }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.ROLL_LENGTH_VARIATION]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3} id="marker">
                  <Card className={classes.border}>
                    <CardHeader title="Marker Efficiency" />
                    <CardContent style={{ padding: 0 }}>
                      <ReactEchartsCore
                        echarts={echarts}
                        option={chartOptions[DataType.MARKER_EFFICIENCY]}
                        style={{ height: "250px", width: "100%" }}
                        opts={{ renderer: "svg" }}
                        className="react_for_echarts"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </div>
          {Object.keys(chartOptions).length > 0 && (
            <Grid id="row-5" container spacing={2} alignItems="stretch">
              <Grid item xs={12} sm={8}>
                <Card className={classnames(classes.marginVertical, classes.border)}>
                  <CardHeader title={`Fabric Utilization Details`} />
                  <PerfectScrollbar>
                    <Table
                      classes={{ table: classes.table }}
                      columns={colsFabUtilization}
                      rows={chartOptions[DataType.FABRIC_UTILIZATION_DETAILS] || []}
                      cellStyle={({ record, column }) =>
                        record.classification === "Fabric Available For Use"
                          ? { color: "green" }
                          : (record.classification === "Missing Fabric" || record.classification === "Unused Fabric") && record.fabric > 0
                          ? { color: "red" }
                          : {}
                      }
                    />
                  </PerfectScrollbar>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <div>
                  <Grid container>
                    <Grid item xs={12}>
                      <Card className={classnames(classes.marginVertical, classes.border)}>
                        <CardHeader title="Highlights" />
                        <Divider />
                        <PerfectScrollbar style={{ height: 195 }}>
                          <List data={highlightList} dense={false} rightColor={colorTomatoRed.main} />
                        </PerfectScrollbar>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card className={classnames(classes.marginVertical, classes.border)}>
                        <CardHeader title="Other Informations" />
                        <Divider />
                        <PerfectScrollbar style={{ height: 190 }}>
                          <List data={otherInfoList} dense={false} rightColor={colorSecondary.main} rightVariant="subtitle1" />
                        </PerfectScrollbar>
                      </Card>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            </Grid>
          )}

          <div>
            <Card id="remarks" className={classnames(classes.marginVertical, classes.border)}>
              <CardHeader title="Remarks" />
              <PerfectScrollbar>
                <Table classes={{ table: classes.table }} columns={colsRemarks} rows={rowsRemarks} />
              </PerfectScrollbar>
            </Card>
          </div>
          <PageFooter>
            <Button label="Print" variant="contained" color="primary" disabled={printing} onClick={onPrint}>
              {printing ? <CircularProgress size={25} thickness={2} color="secondary" /> : <ActionPrint />}
            </Button>
            <BackButton variant="outlined" style={{ marginLeft: "1.5em" }} />
          </PageFooter>
        </div>
      )}
    </div>
  );
};

export default translate(ReconciliationReportDetail);
